'use client'

import { useEffect, useRef, useState } from 'react'

type IdleWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number
  cancelIdleCallback?: (handle: number) => void
}

/**
 * Defers decorative hero video downloads until the document has loaded and the
 * hero is near the viewport. Posters remain the initial/LCP content.
 */
export function useDeferredHeroMedia() {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [isActive, setIsActive] = useState(false)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const idleWindow = window as IdleWindow
    let isNearViewport = false
    let pageLoaded = document.readyState === 'complete'
    let idleHandle: number | undefined
    let fallbackHandle: number | undefined
    let scheduled = false

    const activate = () => {
      if (!isNearViewport || !pageLoaded || scheduled) return
      scheduled = true

      if (idleWindow.requestIdleCallback) {
        idleHandle = idleWindow.requestIdleCallback(
          () => setIsActive(true),
          { timeout: 2000 }
        )
      } else {
        fallbackHandle = window.setTimeout(() => setIsActive(true), 1200)
      }
    }

    const handleLoad = () => {
      pageLoaded = true
      activate()
    }

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver(
          ([entry]) => {
            if (!entry.isIntersecting) return
            isNearViewport = true
            observer?.disconnect()
            activate()
          },
          { threshold: 0.01, rootMargin: '200px' }
        )
      : null

    if (observer) {
      observer.observe(container)
    } else {
      isNearViewport = true
      activate()
    }
    if (!pageLoaded) window.addEventListener('load', handleLoad, { once: true })

    return () => {
      observer?.disconnect()
      window.removeEventListener('load', handleLoad)
      if (idleHandle !== undefined) idleWindow.cancelIdleCallback?.(idleHandle)
      if (fallbackHandle !== undefined) window.clearTimeout(fallbackHandle)
    }
  }, [])

  return { containerRef, isActive }
}
